import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Phone, MapPin, Send, Globe } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Contact form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-stone-50 py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm font-medium text-emerald-700 mb-2">Customer Service</p>
          <h1 className="text-4xl font-bold text-stone-900 mb-4">NUGL Media</h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Have a question or need assistance? We're here to help! Reach out to us through any of the methods below.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Form */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-stone-900 mb-6">Send Us a Message</h2>
            
            {submitted && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg mb-6">
                Thank you! We'll get back to you soon.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-stone-900 mb-2">
                  Your Name *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="h-12"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-900 mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-12"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-900 mb-2">
                  Subject *
                </label>
                <Input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="h-12"
                  placeholder="How can we help?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-900 mb-2">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Tell us more about your inquiry..."
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </Button>
            </form>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-stone-900 mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900 mb-1">Phone</h3>
                    <a href="tel:7143839982" className="text-emerald-700 font-medium hover:underline">
                      714.383.9982
                    </a>
                    <p className="text-sm text-stone-500 mt-1">Mon-Fri: 9AM - 5PM PST</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900 mb-1">Email</h3>
                    <a href="mailto:info@nugl.com" className="text-emerald-700 font-medium hover:underline">
                      info@nugl.com
                    </a>
                    <p className="text-sm text-stone-500 mt-1">We typically respond within 24-48 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900 mb-1">Website</h3>
                    <a href="https://www.nugl.com" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-medium hover:underline">
                      www.nugl.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900 mb-1">Address</h3>
                    <p className="text-stone-600">
                      NUGL Media<br />
                      4195 Chino Hills Pkwy 175<br />
                      Chino Hills, CA 91709
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-8 bg-emerald-50 border-emerald-200">
              <h3 className="text-xl font-bold text-stone-900 mb-4">Business Inquiries</h3>
              <p className="text-stone-700 mb-4">
                Interested in listing your business in our Garden Hub directory or exploring partnership opportunities?
              </p>
              <Button
                onClick={() => window.location.href = '/list-your-business'}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                List Your Business
              </Button>
            </Card>

            <Card className="p-8">
              <h3 className="text-xl font-bold text-stone-900 mb-4">Write for Us</h3>
              <p className="text-stone-700 mb-4">
                Interested in submitting a guest article? We accept paid guest articles on gardening topics.
              </p>
              <Button
                onClick={() => window.location.href = '/write-for-us'}
                variant="outline"
                className="w-full border-emerald-700 text-emerald-700 hover:bg-emerald-50"
              >
                View Submission Guidelines
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
